import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import Select from 'react-select'
import { toast } from 'sonner'

import { useGetEmployerStripePrices } from 'components/hooks/useGetEmployerStripePrices'
import { useGetStripePrices } from 'components/hooks/useGetStripePrices'
import { useUpdateStripePriceIds } from 'components/hooks/useUpdateStripePriceIds'
import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { Banner, Button } from 'slab'
import { cn, formatMoney } from 'utils'

import StripePriceLinks from './StripePriceLinks'
import { type Option, type StripePrice } from './types'

const formatPriceLabel = (price: StripePrice) => {
  const interval = price.recurring?.interval ? `per ${price.recurring.interval}` : 'one time'
  return `${price.id} | ${price.product.name} | ${price.nickname ? `${price.nickname} |` : ''} ${formatMoney(price.unit_amount / 100)} ${interval}`
}

export const StripePriceSelectImpl = ({
  employerId,
  pricingDisabled,
  includeButton,
  includeLinks,
  useDefaultPriceIds,
}: {
  employerId: string,
  pricingDisabled: boolean,
  includeButton: boolean,
  includeLinks: boolean,
  useDefaultPriceIds: boolean,
}) => {
  const { data: prices, isLoading: isPricesLoading } = useGetStripePrices()
  const { data: employerPrices, isLoading: isEmployerPricesLoading } = useGetEmployerStripePrices(employerId)
  const updateStripePriceIdsMutation = useUpdateStripePriceIds()
  const queryClient = useQueryClient()

  const [selectedPrices, setSelectedPrices] = useState<Option[]>([])
  const [apiError, setApiError] = useState<string | null>(null)
  const [options, setOptions] = useState<Option[]>([])

  const shouldDisable = pricingDisabled || selectedPrices.length === 0

  useEffect(() => {
    const stripePriceIds = employerPrices || []
    if (stripePriceIds.length) {
      const selectedOptions = options.filter((option) => stripePriceIds.includes(option.value))
      setSelectedPrices(selectedOptions)
    } else if (useDefaultPriceIds) {
      const defaultOptions = options.filter(option =>
        prices?.some(price => price.id === option.value && price.default)
      )
      setSelectedPrices(defaultOptions)
    }
  }, [employerPrices, options, prices, useDefaultPriceIds])

  useEffect(() => {
    const options = prices?.map((price) => ({ label: formatPriceLabel(price), value: price.id })) || []
    setOptions(options)
  }, [prices])

  useEffect(() => {
    const hiddenInput = document.querySelector<HTMLInputElement>('input[name="employer[stripe_price_ids]"]')!
    if (hiddenInput) {
      const prices = selectedPrices.map(option => option.value).join(',')
      hiddenInput.value = prices
    }
  }, [selectedPrices])

  const handleUpdateStripePriceIds = () => {
    setApiError(null)
    updateStripePriceIdsMutation.mutate({
      employer_id: employerId,
      stripe_price_ids: selectedPrices.map((option) => option.value),
    }, {
      onSuccess: () => {
        toast.success('Price ids updated successfully')
        queryClient.invalidateQueries({ queryKey: ['employerStripePrices', employerId] })
      },
      onError: (error) => {
        setApiError(error.message)
      }
    })
  }

  if (isEmployerPricesLoading || isPricesLoading) {
    return (
      <div className='bg-white p-4 rounded-md shadow-sm mb-4'>
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <>
      {apiError && (
        <Banner
          type='critical'
          title='Error updating price ids'
        >
          {apiError}
        </Banner>
      )}
      <div className={cn({
        'flex flex-col gap-4': true,
        'mt-6': includeLinks,
      })}
      >
        {includeLinks && <StripePriceLinks prices={prices ?? []} selectedPriceIds={selectedPrices} />}
        <div className='flex gap-4'>
          <div className='flex-col flex-grow w-full'>
            <Select
              className='appearance-none w-full rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
              isDisabled={pricingDisabled}
              isMulti
              isSearchable
              value={selectedPrices}
              options={options}
              onChange={(value) => setSelectedPrices(value as Option[])}
              inputId='select-input'
              instanceId='dropdown'
              aria-label='Select prices'
              placeholder='Select prices'
            />
          </div>
          {includeButton && (
            <div className='inline-flex items-end'>
              <TooltipWrapper
                tooltipText={pricingDisabled ? 'This employer has PEPMs disabled' : 'Must have at least one price selected'}
                theme='thatch-purple'
                hidden={!shouldDisable}
              >
                <Button
                  variant='primary'
                  data-testid='update-price-ids-button'
                  disabled={shouldDisable}
                  onClick={handleUpdateStripePriceIds}
                >
                  Update
                </Button>
              </TooltipWrapper>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

interface StripePriceSelectProps {
  employerId: string;
  pricingDisabled?: boolean;
  includeButton?: boolean;
  includeLinks?: boolean;
  useDefaultPriceIds?: boolean;
}

const StripePriceSelect = ({
  employerId,
  pricingDisabled = false,
  includeButton = true,
  includeLinks = true,
  useDefaultPriceIds = false,
}: StripePriceSelectProps) => {
  return (
    <StripePriceSelectImpl
      employerId={employerId}
      pricingDisabled={pricingDisabled}
      includeButton={includeButton}
      includeLinks={includeLinks}
      useDefaultPriceIds={useDefaultPriceIds}
    />
  )
}

export default StripePriceSelect
